import React from 'react';
import { motion } from 'framer-motion';

interface CompatibilidadMusicalProps {
  myTopTracks: any[];
  myTopArtists: any[];
  herTopTracks: any[];
  herTopArtists: any[];
}

interface CompatibilityResult {
  overall: number;
  genrePercentage: number;
  musicPercentage: number;
  commonGenres: string[];
  commonArtists: string[];
  commonTracks: string[];
}

// Componente de tarjeta de compatibilidad
const CompatibilityCard = ({ 
  title, 
  icon, 
  value, 
  description, 
  items = [] 
}: { 
  title: string, 
  icon: string, 
  value: string, 
  description: string, 
  items?: string[] 
}) => (
  <div className="bg-gradient-to-br from-green-500/10 to-pink-500/10 p-4 rounded-lg border border-white/10">
    <div className="text-center mb-3">
      <div className="text-3xl mb-2">{icon}</div>
      <h4 className="font-bold text-green-400">{title}</h4>
      <div className="text-2xl font-bold text-white my-2">{value}</div>
      <p className="text-xs text-gray-400">{description}</p>
    </div>
    {items.length > 0 && (
      <div className="mt-3 space-y-1">
        {items.slice(0, 3).map((item, index) => (
          <div key={index} className="text-xs bg-white/5 p-2 rounded text-center">
            {item}
          </div>
        ))}
      </div>
    )}
  </div>
);

export const CompatibilidadMusical: React.FC<CompatibilidadMusicalProps> = ({ 
  myTopTracks, 
  myTopArtists,
  herTopTracks, 
  herTopArtists 
}) => {
  // ALGORITMO DE COMPATIBILIDAD ROMÁNTICO (80-100%) - CORREGIDO
  const calculateCompatibility = (): CompatibilityResult | null => {
    if (!myTopTracks.length || !herTopTracks.length || !myTopArtists.length || !herTopArtists.length) return null;

    // 1. COMPATIBILIDAD DE GÉNEROS (peso: 40%) - ALGORITMO CORREGIDO
    const myGenres = Array.from(new Set(myTopArtists.flatMap(artist => artist.genres || [])));
    const herGenres = Array.from(new Set(herTopArtists.flatMap(artist => artist.genres || [])));
    
    const commonGenres = myGenres.filter(genre => herGenres.includes(genre));
    const uniqueCommonGenres = Array.from(new Set(commonGenres));
    
    // Calcular compatibilidad como porcentaje de géneros en común vs total de géneros únicos de ambos
    const totalUniqueGenres = Array.from(new Set([...myGenres, ...herGenres])).length;
    const genreCompatibility = totalUniqueGenres > 0 ? 
      (uniqueCommonGenres.length / totalUniqueGenres) * 100 : 0;

    // 2. COMPATIBILIDAD DE ARTISTAS (peso: 35%) - CORREGIDO
    const myArtistNames = Array.from(new Set(myTopArtists.map(artist => artist.name.toLowerCase())));
    const herArtistNames = Array.from(new Set(herTopArtists.map(artist => artist.name.toLowerCase())));
    
    const commonArtists = myArtistNames.filter(artist => herArtistNames.includes(artist));
    const uniqueCommonArtists = Array.from(new Set(commonArtists));
    
    const totalUniqueArtists = Array.from(new Set([...myArtistNames, ...herArtistNames])).length;
    const artistCompatibility = totalUniqueArtists > 0 ? 
      (uniqueCommonArtists.length / totalUniqueArtists) * 100 : 0;

    // 3. COMPATIBILIDAD DE CANCIONES (peso: 25%) - CORREGIDO
    const myTrackNames = Array.from(new Set(myTopTracks.map(track => track.name?.toLowerCase().trim())));
    const herTrackNames = Array.from(new Set(herTopTracks.map(track => track.name?.toLowerCase().trim())));
    
    const commonTracks = myTrackNames.filter(track => herTrackNames.includes(track));
    
    const totalUniqueTracks = Array.from(new Set([...myTrackNames, ...herTrackNames])).length;
    const trackCompatibility = totalUniqueTracks > 0 ? 
      (commonTracks.length / totalUniqueTracks) * 100 : 0;

    // 4. CÁLCULO FINAL CON PESOS Y RANGO 80-100%
    const weightedCompatibility =
      (genreCompatibility * 0.4) +
      (artistCompatibility * 0.35) +
      (trackCompatibility * 0.25);

    // Mapear de 0-100% a 80-100% para mantener la magia 💕
    const romanticCompatibility = Math.round(80 + (weightedCompatibility * 0.2));

    // Aplicar el mismo algoritmo romántico a "Música en Común"
    const musicAverage = (artistCompatibility + trackCompatibility) / 2;
    const romanticMusicPercentage = Math.round(80 + (musicAverage * 0.2));

    // Aplicar también a géneros para consistencia
    const romanticGenrePercentage = Math.round(80 + (genreCompatibility * 0.2));

    console.log('🎵 Debug compatibilidad:', {
      genreCompatibility: Math.round(genreCompatibility),
      artistCompatibility: Math.round(artistCompatibility),
      trackCompatibility: Math.round(trackCompatibility),
      weightedCompatibility: Math.round(weightedCompatibility),
      romanticCompatibility,
      romanticMusicPercentage,
      romanticGenrePercentage,
      myGenres: myGenres.slice(0, 5),
      herGenres: herGenres.slice(0, 5),
      commonGenres: uniqueCommonGenres
    });

    return {
      overall: Math.min(100, Math.max(80, romanticCompatibility)),
      genrePercentage: Math.min(100, Math.max(80, romanticGenrePercentage)),
      musicPercentage: Math.min(100, Math.max(80, romanticMusicPercentage)),
      commonGenres: uniqueCommonGenres,
      commonArtists: uniqueCommonArtists,
      commonTracks
    };
  };

  const compatibility = calculateCompatibility();

  // Si no hay datos suficientes, no mostrar nada
  if (!compatibility) {
    return null;
  }

  return (
    <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>
      <h3 className="text-2xl font-bold text-center mb-6 text-green-400">
        💕 Compatibilidad Musical
      </h3>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <CompatibilityCard 
          title="Compatibilidad General"
          icon="💖"
          value={`${compatibility.overall}%`}
          description="Vuestra conexión musical"
        />
        <CompatibilityCard 
          title="Géneros Similares"
          icon="🎼"
          value={`${compatibility.genrePercentage}%`}
          description="Gustos musicales en común"
          items={compatibility.commonGenres}
        />
        <CompatibilityCard 
          title="Música en Común"
          icon="🎵"
          value={`${compatibility.musicPercentage}%`}
          description="Artistas y canciones compartidas"
        />
      </div>
    </motion.div>
  );
};